import React, { useEffect, useRef, memo } from 'react';

interface Particle {
  x: number;
  y: number;
  size: number;
  speedX: number;
  speedY: number;
  opacity: number;
}

const ParticlesComponent: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    // Disable particles on mobile devices (< 768px)
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      console.log('Particles disabled on mobile devices');
      return;
    }

    // Performance detection
    const hardwareCores = navigator.hardwareConcurrency || 4;
    const isLowPerformance = hardwareCores < 4;

    // Check connection speed if available
    const nav = navigator as any;
    const hasSlowConnection = nav.connection && 
      ['slow-2g', '2g', '3g'].includes(nav.connection.effectiveType);

    // Don't render particles on low-performance devices
    if (isLowPerformance || hasSlowConnection) {
      console.log('Particles disabled due to low performance detection');
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    const particles: Particle[] = [];
    // Reduce particle count on lower-end devices
    const particleCount = hardwareCores >= 6 ? 30 : 20;

    // Create particles
    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 2 + 1,
        speedX: (Math.random() - 0.5) * 0.5,
        speedY: (Math.random() - 0.5) * 0.5,
        opacity: Math.random() * 0.5 + 0.2,
      });
    }

    let lastTime = 0;
    // Reduce FPS on lower-end devices
    const fps = hardwareCores >= 6 ? 60 : 30;
    const interval = 1000 / fps;

    const animate = (currentTime: number) => {
      const deltaTime = currentTime - lastTime;

      if (deltaTime >= interval) {
        lastTime = currentTime - (deltaTime % interval);

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        particles.forEach((particle) => {
          // Update position
          particle.x += particle.speedX;
          particle.y += particle.speedY;

          // Wrap around edges
          if (particle.x > canvas.width) particle.x = 0;
          if (particle.x < 0) particle.x = canvas.width;
          if (particle.y > canvas.height) particle.y = 0;
          if (particle.y < 0) particle.y = canvas.height;

          // Draw particle with gold glow
          ctx.beginPath();
          ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
          
          // Gold gradient
          const gradient = ctx.createRadialGradient(
            particle.x, particle.y, 0,
            particle.x, particle.y, particle.size * 3
          );
          gradient.addColorStop(0, `rgba(255, 215, 0, ${particle.opacity})`);
          gradient.addColorStop(0.5, `rgba(255, 140, 0, ${particle.opacity * 0.5})`);
          gradient.addColorStop(1, 'rgba(255, 0, 0, 0)');
          
          ctx.fillStyle = gradient;
          ctx.fill();
        });
      }

      requestAnimationFrame(animate);
    };

    requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-0"
      style={{ opacity: 0.4, willChange: 'transform' }}
    />
  );
};

export const Particles = memo(ParticlesComponent);
Particles.displayName = 'Particles';
